/**
 * Avatar — shared avatar display used across the app.
 *
 * Handles three avatar types:
 *   - Photo (data: URI or https URL) — shown as a circular image
 *   - Emoji (single character/emoji) — shown large and centred
 *   - Initials fallback             — first letter of name on ice-tinted circle
 *
 * Props:
 *   avatarUrl  string | null  — the user's avatar value
 *   name       string | null  — used for the initials fallback
 *   size       number         — diameter in px (default 32)
 *   className  string         — optional extra class
 */
export default function Avatar({ avatarUrl, name, size = 32, className = '' }) {
  const isPhoto = avatarUrl && (avatarUrl.startsWith('data:') || avatarUrl.startsWith('http'));
  const isEmoji = avatarUrl && !isPhoto;

  return (
    <div
      className={className}
      style={{
        width: size, height: size,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
        background: isPhoto ? '#000' : 'var(--ice-a15)',
        border: '1.5px solid var(--ice-a25)',
        fontSize: isEmoji ? size * 0.52 : size * 0.4,
        fontWeight: 700,
        color: 'var(--ice)',
        userSelect: 'none',
      }}
    >
      {isPhoto && (
        <img
          src={avatarUrl}
          alt={name || 'avatar'}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          loading="lazy"
        />
      )}
      {isEmoji && avatarUrl}
      {!avatarUrl && (name?.[0]?.toUpperCase() || '?')}
    </div>
  );
}
